import type {
  Id,
  EditableNode,
  EditableGraph,
  JobSearchParam,
  Criteria,
} from 'src/types/chatbot';

import values from 'lodash/values';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';

import {setError, type Validation} from 'src/utils/validation';
import {hasText, testRegex} from 'src/utils/draft';
import {smsKeywords} from 'src/utils/sms';


const normalizeNodeName = (name: string) =>
  name.toLowerCase().trim().replace(/\s+/g, '_'); // Replace multiple spaces with single underscore, preserve all characters

export function validateNode<N extends EditableNode>(
  node: N,
  graph: EditableGraph,
  jobSearchParams?: Array<JobSearchParam> | null,
  sortParams?: Array<{
    depends_on_either: Array<Array<Criteria>>;
    value: string;
    label?: string;
    key: string;
  }> | null,
): Validation | null | undefined {
  // @ts-expect-error - TS7034 - Variable 'error' implicitly has type 'any' in some locations where its type cannot be determined.
  let error;

  error = setError(error, node.name, 'name', 'EMPTY');

  error = setError(
    error,
    ![...graph.nodes.values()].some(
      (otherNode) =>
        otherNode.id !== node.id &&
        normalizeNodeName(otherNode.name) === normalizeNodeName(node.name),
    ),
    'name',
    'DUPLICATE',
  );

  if (
    node.type === 'question' ||
    node.type === 'message' ||
    node.type === 'scheduler'
  ) {
    error = setError(
      error,
      hasText(node.text_draft_json),
      'text_draft_json',
      'EMPTY',
    );

    error = setError(
      error,
      // @ts-expect-error - TS7006 - Parameter 'block' implicitly has an 'any' type.
      !node.text_draft_json.blocks.some((block) => block.text.includes('$<')),
      'text_draft_json',
      'INVALID_VARIABLE_PREFIX',
    );

    error = setError(
      error,
      !testRegex(node.text_draft_json, /<<[^>]+>>/),
      'text_draft_json',
      'BAD_VARIABLE',
    );
  }

  if (node.type === 'text_to_apply') {
    error = setError(
      error,
      Number(node.answers?.length) > 1,
      'answers',
      'EMPTY',
    );
  }

  if (node.type === 'disposition') {
    error = setError(
      error,
      node.writeback_field_id,
      'writeback_field_id',
      'EMPTY',
    );
    error = setError(
      error,
      (node.text_draft_json && hasText(node.text_draft_json)) ||
        node.disposition_writeback_value,
      'text_draft_json',
      'DISPOSITION_VALUE_EMPTY',
    );
  }

  if (node.type === 'notification') {
    error = setError(
      error,
      node.notification_to_recipients
        ? node.notification_to_recipients.length
        : 0,
      'notification_to_recipients',
      'EMPTY',
    );
    error = setError(
      error,
      node.notification_email_subject.blocks[0].text.length,
      'notification_email_subject',
      'EMPTY',
    );
  }

  if (node.type === 'question') {
    const {answers} = node;
    if (answers) {
      const enabledAnswers = answers.filter((answer) => !answer.disabled);
      const uniqueAnswers = uniqBy(enabledAnswers, 'label');

      error = setError(
        error,
        uniqueAnswers.length === enabledAnswers.length, //disabled answers should not be part of this validation
        'answers',
        'DUPLICATE',
      );

      // SMS chatbots cannot override carrier keywords (e.g. 'STOP')
      if (graph.medium === 'sms') {
        const smsKeywordsSet = new Set(smsKeywords);
        for (const answer of enabledAnswers) {
          error = setError(
            error,
            // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"cancel" | "stop" | "start" | "end" | "help" | "unsubscribe" | "quit"'.
            !smsKeywordsSet.has(answer.label.toLowerCase().trim()),
            `answers[${answer.id}]`,
            'SMS_KEYWORD',
          );
        }
      }
    }
  }

  if (node.type === 'scheduler') {
    error = setError(
      error,
      node.scheduler_data.type === 'dynamic_template' ||
        node.scheduler_template_id,
      'scheduler_template_id',
      'EMPTY',
    );

    let eventInfoValidation;
    if (node.scheduler_data.event_info) {
      const emptyEventInfo =
        node.scheduler_data.event_info.event_title &&
        !hasText(node.scheduler_data.event_info.event_title) &&
        node.scheduler_data.event_info.event_description &&
        !hasText(node.scheduler_data.event_info.event_description);
      const completedEventInfo =
        node.scheduler_data.event_info?.event_title &&
        hasText(node.scheduler_data.event_info.event_title) &&
        node.scheduler_data.event_info.event_description &&
        hasText(node.scheduler_data.event_info.event_description);
      eventInfoValidation = emptyEventInfo || completedEventInfo;
    } else {
      eventInfoValidation = true;
    }

    error = setError(error, eventInfoValidation, 'event_info', 'EMPTY');

    error = setError(
      error,
      node.scheduler_data.type === 'static_template' ||
        node.scheduler_data.global_variable,
      'scheduler_mapping_var',
      'EMPTY',
    );

    for (const [index, question] of node.scheduler_data.contacts.entries()) {
      error = setError(
        error,
        question.node_id || question.dynamic_field,
        `scheduler_template_contacts[${index}]`,
        'EMPTY',
      );
    }
    for (const [index, question] of node.scheduler_data.questions.entries()) {
      error = setError(
        error,
        question.node_id || question.dynamic_field,
        `scheduler_template_questions[${index}]`,
        'EMPTY',
      );
    }
    for (const [
      index,
      question,
    ] of node.scheduler_data.additional_questions.entries()) {
      error = setError(
        error,
        question.node_id || question.dynamic_field,
        `scheduler_template_additional_questions[${index}]`,
        'EMPTY',
      );
    }
  }

  if (node.type === 'condition') {
    const {expressions} = node.condition_ruleset;
    for (const [index, expression] of expressions.entries()) {
      if (['greater_than', 'less_than'].includes(expression.operator)) {
        error = setError(
          error,
          Boolean(expression.condition_value.length),
          `expressions[${index}]`,
          `Comparison value required`,
        );
      }
    }
  }

  if (node.type === 'job_match') {
    const criterion = values(node.job_search_criteria_data.criteria);

    const sortParamMeta = sortParams?.find(
      ({value}) => value === node.job_search_criteria_data.sort_param,
    );
    if (sortParamMeta?.depends_on_either.length) {
      const hasValue = sortParamMeta.depends_on_either.some((set) =>
        set.every(
          (param) =>
            node.job_search_criteria_data.criteria[param] &&
            criteriaHasValue(node.job_search_criteria_data.criteria[param]),
        ),
      );
      const dependencies = sortParamMeta.depends_on_either
        .map((set) =>
          set
            .map(
              (param) =>
                jobSearchParams?.find(({id}) => id === param)?.name ?? param,
            )
            .join(' and '),
        )
        .join(' or ');

      error = setError(
        error,
        hasValue,
        'sort_param_dependency',
        `Sort by ${
          sortParamMeta.label ?? sortParamMeta.value
        } requires ${dependencies}`,
      );
    }

    error = setError(
      error,
      criterion.find(criteriaHasValue) ||
        node.job_search_criteria_data.resume_node_id,
      `criteria`,
      'EMPTY',
    );

    error = setError(
      error,
      !isEmpty(node.job_search_criteria_data.matching_messages_rte) &&
        hasText(node.job_search_criteria_data.matching_messages_rte),
      'matching_message',
      'EMPTY,',
    );
    error = setError(
      error,
      hasText(node.job_search_criteria_data.no_matches_message_rte),
      'no_matches_message',
      'EMPTY,',
    );

    if (jobSearchParams) {
      jobSearchParams.forEach((criteriaData) => {
        const criteria =
          node.job_search_criteria_data.criteria[criteriaData.id];
        if (criteria && criteriaHasValue(criteria)) {
          const passValidation =
            !criteriaData.depends_on_either.length ||
            criteriaData.depends_on_either.some((dependency) =>
              dependency.every((param) =>
                criteriaHasValue(node.job_search_criteria_data.criteria[param]),
              ),
            );
          const dependencyDisplay = criteriaData.depends_on_either
            .map((dependency) => {
              const labels = dependency.map(
                (param) =>
                  jobSearchParams.find(({id}) => id === param)?.name ?? param,
              );
              return labels.join(' and ');
            })
            .join(' or ');
          error = setError(
            // @ts-expect-error - TS7005 - Variable 'error' implicitly has an 'any' type.
            error,
            passValidation,
            'criteria_dependency',
            `${criteriaData.name} requires ${dependencyDisplay}`,
          );
        }
      });
    }
  }
  return error;
}

// @ts-expect-error - TS7006 - Parameter 'criteria' implicitly has an 'any' type.
const criteriaHasValue = (criteria) =>
  criteria.reference_node_ids.length > 0 ||
  criteria.ats_variables.length > 0 ||
  (criteria.default_values?.length ?? 0) > 0;

const numberRegex = /^[0-9\., ]+$/;
